/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Col, Row } from 'react-bootstrap';
import { get, isEqual } from 'lodash';

import ComponentHeader from '../../components/Headers/component-header';

import DivergingBar from '../../components/Charts/EmotionDivergingBar';

import HottestMoments from '../../components/HottestMoments';
import InlineQuery from '../../components/InlineQuery';
import TabsButtonGroup from '../../components/TabsButtonGroup';
import ActorItem from '../../components/SectionedItems/ActorItem';
import SectionedItems from '../../components/SectionedItems';

import { showReportEngagement } from '../../config/filters';
import { EmotionHeatmap } from '../../components/Charts/EmotionHeatmap';
import Table from '../../components/Table';
import Reactions from '../../components/Reactions';
import { FilterableLineChart } from '../../components/Charts/FilterableLineChart';
import {
  engagementTotalColumns,
  engagementTotalDefaultSorts,
  formatCharacterVoteByEpisode,
  formatEmotionVotesByEpisode
} from './table-config';
import {
  formatEmotionData,
  emotionsAxisElement,
  getEmotionVotes
} from '../../utils/emotions';

import { formatActorsFromQueryNew } from '../../utils/show';
import showsConfig from '../configs/shows-config';
import { extractFilterValues } from '../../utils/filters';
import ModuleExport from '../../components/ModuleExport';
import Blocker from '../../components/Blocker';
import emotionsIcon from '../../assets/images/emotions.gif';
import barchartIcon from '../../assets/images/icons/bar-chart-icon.png';
import spreadsheetIcon from '../../assets/images/icons/spreadsheet-icon.png';
import { FilterTypes } from '../../components/Filters';

const ReactionsQuery = {
  id: 'showReactions',
  meta: {
    fields: [
      'show',
      'characters',
      'ratings',
      'reactionType',
      'parentComment',
      'country',
      'date',
      'episode',
      'season',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'originating_language',
      'emotions'
    ]
  },
  sortOptions: [
    { label: 'Most Engagements', value: 'comment_feed.engagements desc' },
    { label: 'Most Recent', value: 'comment_feed.date desc' }
  ]
};

const ReactionsCountQuery = {
  id: 'showReactionsCount',
  meta: {
    fields: [
      'show',
      'characters',
      'ratings',
      'reactionType',
      'country',
      'date',
      'episode',
      'season',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'originating_language',
      'emotions'
    ]
  }
};

const ReactionsEmotionQuery = {
  id: 'showReactionsEmotions',
  meta: {
    fields: [
      'show',
      'country',
      'date',
      'episode',
      'season',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'emotions'
    ]
  }
};

const ReactionsCharacterQuery = {
  id: 'showReactionsCharacters',
  meta: {
    fields: [
      'show',
      'country',
      'date',
      'episode',
      'season',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'characters'
    ]
  }
};

const ReactionsRatingQuery = {
  id: 'showReactionsRating',
  meta: {
    fields: [
      'show',
      'country',
      'date',
      'episode',
      'season',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'ratings'
    ]
  }
};

const ShowEngagement = ({
  engagementQueries: {
    characterVoteEpisodeRank,
    hottestMoments,
    showEmotionIndex,
    showEmotionIndexMeta,
    showEpisodeEmotions,
    showEpisodeEmotionsMeta,
    memesList,
    engagementRawTotals,
    showFavorites
  },
  updateBenchmarkData,
  fetchMemes,
  fetchMemesStatus,
  show,
  filters,
  queryActions: { runFormattedInlineQueriesForPage },
  blocked,
  updateEpisodeOptionsLoader
}) => {
  const [selectedFilters, setSelectedFilters] = useState([]);

  const updateQueries = () => {
    runFormattedInlineQueriesForPage(
      showsConfig.SHOW_REPORT_FILTERS[showsConfig.SHOW_ENGAGEMENT_PAGE_ID],
      filters.fields
    );
    updateBenchmarkData(filters.fields, null, true);
  };

  const noFeedbackMessage =
    'We are still collecting feedback from our viewers. Stay tuned!';
  const loadingVotes = get(showEmotionIndexMeta, 'loading', true);
  const emotionVotesInfo = getEmotionVotes(showEmotionIndexMeta);
  const emotionEpisodeInfo = getEmotionVotes(showEpisodeEmotionsMeta);

  const getDataElement = () => {
    const elemId = `tvt-tabs-show-engagement-reactions-pane-0`;
    return document
      .getElementById(elemId)
      .getElementsByClassName('exportable')[0];
  };

  const downloadReportFunc = () => (api, type, title) => {
    const reactionsTab = 'hottestMoments';

    return api(
      {
        view: reactionsTab,
        filters: get(filters, 'fields', []),
        queryMeta: showReportEngagement.queries[reactionsTab]
      },
      type,
      title
    );
  };

  const tableDownload = viewName => (api, type, title) => {
    return api(
      {
        view: viewName,
        filters: get(filters, 'fields', []),
        queryMeta: showReportEngagement.queries[viewName]
      },
      type,
      title
    );
  };

  const emotionsQueryContent = emotionVotesInfo ? (
    <>
      <ModuleExport
        fetchData
        tableDataDownload={tableDownload('showEmotionIndex')}
        data={() => {}}
        title="Show Emotion Index"
        showTitle={show.name}
        csv
        xls
        png
      />
      <InlineQuery query={showEmotionIndex}>
        {query => (
          <DivergingBar
            width={1158}
            axisElement={emotionsAxisElement(query)}
            query={formatEmotionData(query)}
          />
        )}
      </InlineQuery>
    </>
  ) : (
    <Blocker
      icon={emotionsIcon}
      className="tvt-blocker__tab-content-wrapped"
      message={noFeedbackMessage}
    />
  );

  useEffect(() => {
    if (blocked) {
      return;
    }

    const values = extractFilterValues(filters.fields);
    const hasNewValues = !isEqual(values, selectedFilters);

    if (hasNewValues) {
      updateEpisodeOptionsLoader(get(filters, 'fields.season.value', []));
      setSelectedFilters(values);
      updateQueries();
    }
  }, [blocked, filters.fields]);

  return (
    <Grid fluid componentClass="section">
      <Row>
        <Col className="tvt-show-engagement__characters-wrapper" xs={12}>
          <ComponentHeader title="Hottest Characters" />
          <TabsButtonGroup id="tvt-show-engagement__characterss">
            <div icon="OVERALL">
              <ModuleExport
                fetchData
                tableDataDownload={tableDownload('showFavorites')}
                data={() => {}}
                title="Hottest Characters Overall"
                showTitle={show.name}
                csv
                xls
                png
              />
              <InlineQuery useChildForLoading query={showFavorites}>
                {query => (
                  <SectionedItems
                    className="tvt-show-engagement__fan-favorites"
                    hasSeparators={false}
                    itemType={ActorItem}
                    list={formatActorsFromQueryNew(query)}
                    loading={showFavorites.loading}
                    actorLoader
                  />
                )}
              </InlineQuery>
            </div>
            <div icon="BY EPISODE">
              <ModuleExport
                fetchData
                tableDataDownload={tableDownload('characterVoteEpisodeRank')}
                data={() => {}}
                title="Hottest Characters By Episode"
                showTitle={show.name}
                csv
                xls
                png
              />
              <div
                style={{
                  height: '622px',
                  width: '100%',
                  overflow: 'scroll',
                  backgroundColor: '#fff'
                }}
              >
                <FilterableLineChart
                  loading={characterVoteEpisodeRank.loading}
                  data={formatCharacterVoteByEpisode(
                    characterVoteEpisodeRank.data
                  )}
                  axisLeftTitle="Percent of Votes"
                />
              </div>
            </div>
          </TabsButtonGroup>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="tvt-show-engagement__emotions-wrapper">
          <ComponentHeader
            title="Emotion Index"
            tooltip="Emotions are indexed relative to the performance of series that have similar genres"
          />

          <div
            className="hidden"
            style={{
              borderRadius: '4px',
              border: '1px solid #bbb',
              backgroundColor: '#e8e8e8',
              padding: '7px 12px',
              marginBottom: '20px'
            }}
          >
            This data is temporarily unavailable as part of a data
            infrastructure migration.
          </div>

          <div>
            <TabsButtonGroup id="tvt-show-engagement__emotions">
              <div
                info={emotionVotesInfo}
                icon={
                  <img
                    src={barchartIcon}
                    alt="Bar chart"
                    className="tvt-tabs-button-group__tab-icon"
                  />
                }
              >
                {loadingVotes ? (
                  <div className="tvt-show-engagement__loading-spinner">
                    <div className="tvt-time-chart__loading-state-wrapper">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="1029"
                        height="432"
                        viewBox="0 0 1029 432"
                      >
                        <defs>
                          <linearGradient
                            id="9aldktrvoa"
                            x1="0%"
                            x2="100%"
                            y1="50%"
                            y2="50%"
                          >
                            <stop offset="0%" stopColor="#EEEEEE" />
                            <stop offset="33%" stopColor="#EEEEEE" />
                            <stop offset="50%" stopColor="#f5f5f5" />
                            <stop offset="67%" stopColor="#EEEEEE" />
                            <stop offset="100%" stopColor="#EEEEEE" />
                            <animateTransform
                              attributeName="gradientTransform"
                              type="translate"
                              from="-1 0"
                              to="1 0"
                              begin="0s"
                              dur="1.5s"
                              repeatCount="indefinite"
                            />
                          </linearGradient>
                        </defs>
                        <path
                          fill="url(#9aldktrvoa)"
                          fillRule="evenodd"
                          d="M148 448v14H74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zm82 0v14h-74v-14h74zM66 30v405h8V70h74v365h8V125h74v310h8V178h74v257h8V230h74v205h8V275h74v160h8V314h74v121h8v-90h74v90h8v-60h74v60h8v-38h74v38h8v-22h74v22h8v-9h74v9h8v-5h74v5h10v1H65V30h1zM45 198v90H31v-90h14z"
                          transform="translate(-31 -30)"
                        />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      height: '518px',
                      width: '100%',
                      overflow: 'scroll'
                    }}
                  >
                    {emotionsQueryContent}
                  </div>
                )}
              </div>

              <div
                info={emotionEpisodeInfo}
                icon={
                  <img
                    src={spreadsheetIcon}
                    alt="Table"
                    className="tvt-tabs-button-group__tab-icon"
                  />
                }
              >
                <ModuleExport
                  fetchData
                  tableDataDownload={tableDownload('showEpisodeEmotions')}
                  data={() => {}}
                  title="Show Emotion Index"
                  showTitle={show.name}
                  csv
                  xls
                  png
                />
                <div
                  style={{ height: '518px', width: '100%', overflow: 'scroll' }}
                >
                  <EmotionHeatmap
                    data={formatEmotionVotesByEpisode(showEpisodeEmotions.data)}
                    loading={showEpisodeEmotions.loading}
                  />
                </div>
              </div>
            </TabsButtonGroup>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ComponentHeader title="Engagement Overview" />
          <InlineQuery useChildForLoading query={engagementRawTotals}>
            {query => (
              <Table
                data={query.data}
                headerFormatter={engagementTotalColumns}
                defaultSorts={engagementTotalDefaultSorts}
                view="engagementRawTotals"
                filters={filters.fields}
                showRank={false}
                titleName={show.name}
                queryMeta={
                  showsConfig.SHOW_REPORT_FILTERS.showReportEngagement.queries
                    .engagementRawTotals
                }
              />
            )}
          </InlineQuery>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="tvt-show-engagement__reactions-col-wrapper">
          <ComponentHeader title="Reactions" />
          <TabsButtonGroup id="tvt-show-engagement__reactions">
            <div className="tvt-engagement__reactions-pane" icon="FEED">
              <Reactions
                query={ReactionsQuery}
                countQuery={ReactionsCountQuery}
                emotionsQuery={ReactionsEmotionQuery}
                characterQuery={ReactionsCharacterQuery}
                ratingsQuery={ReactionsRatingQuery}
                filters={filters}
                titleName={show.name}
                contentType="show"
              />
            </div>
            <div
              className="tvt-engagement__reactions-pane hottest-moments-wrapper"
              icon="MOMENTS"
            >
              <>
                <ModuleExport
                  fetchData
                  tableDataDownload={downloadReportFunc()}
                  data={() => {}}
                  dataElement={getDataElement}
                  title="Character Vote By Episode"
                  showTitle={show.name}
                  csv
                  xls
                  png
                />
                <InlineQuery useChildForLoading query={hottestMoments}>
                  {query => (
                    <HottestMoments
                      data={query}
                      fetchMemes={fetchMemes}
                      fetchMemesStatus={fetchMemesStatus}
                      memes={memesList}
                    />
                  )}
                </InlineQuery>
              </>
            </div>
          </TabsButtonGroup>
        </Col>
      </Row>
    </Grid>
  );
};

ShowEngagement.propTypes = {
  engagementQueries: PropTypes.shape({
    characterVoteEpisodeRank: PropTypes.shape({}),
    hottestMoments: PropTypes.shape({}),
    showEmotionIndex: PropTypes.shape({}),
    showEmotionIndexMeta: PropTypes.shape({}),
    commentFeed: PropTypes.shape({}),
    memesList: PropTypes.shape({}),
    showEpisodeEmotions: PropTypes.shape({}),
    showEpisodeEmotionsMeta: PropTypes.shape({}),
    engagementRawTotals: PropTypes.shape({}),
    showFavorites: PropTypes.shape({})
  }),
  blocked: PropTypes.bool.isRequired,
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,
  updateEpisodeOptionsLoader: PropTypes.func.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType, // eslint-disable-line react/no-typos
    options: PropTypes.shape({})
  }).isRequired,
  updateBenchmarkData: PropTypes.func.isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired,

  show: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  }).isRequired,
  fetchMemes: PropTypes.func.isRequired,

  episode: PropTypes.shape({}),
  season: PropTypes.shape({}),
  fetchMemesStatus: PropTypes.string
};

ShowEngagement.defaultProps = {
  engagementQueries: {
    engagementRawTotals: {
      data: [],
      fields: {}
    },
    characterVoteEpisodeRank: {
      data: [],
      fields: {}
    },
    showEmotionIndex: {
      data: [],
      fields: {}
    },
    showEmotionVotes: {
      data: [],
      fields: {}
    },
    commentFeed: {
      data: [],
      fields: {}
    },
    hottestMoments: {
      data: [],
      fields: {}
    },
    memesList: {
      data: [],
      fields: {}
    },
    showEpisodeEmotionsMeta: {
      data: [],
      fields: {}
    }
  },
  episode: null,
  season: null,
  fetchMemesStatus: 'notstarted'
};

export default ShowEngagement;
